import type { HTMLAttributes } from 'react';
import React, { useMemo } from 'react';
import { z } from 'zod';
import { kebabCase, merge, zipObject } from 'lodash';
import { Locale, Segment } from '@fixter/i18n';
import type { SchemaType } from '$util/schema';
import { useAppContext } from '$util/AppContext';
import type { ContentComponent, ContentComponentFetchServerSideData } from '$cms/ContentComponent';
import vars from '$util/theme/vars';
import { CmsAreaItemSchema, CmsProductItemSchema, CmsRichTextSchema, CmsShortTextSchema } from '$cms/types';
import type { AppContextGlobal } from '$util/AppContextGlobal';
import { fetchAreaCollectionsData } from '$server/modules/cms/fetchAreaCollectionData';
import { fetchProductCollectionData } from '$server/modules/cms/fetchProductCollectionData';
import { Link } from '$quantum/atoms/Link';
import { renderRichTextContent } from '@/cms/richTextContentHelpers';
import type { ILink } from '@/components/atoms/SimpleLink';
import { getLinksListTitle } from '@/components/organisms/PopularLinksList/utils/getLinksListsTitle';
import {
  makePopularAreaLink,
  makePopularProductLink,
} from '@/components/organisms/PopularLinksList/utils/makePopularLinks';
import { makeViewAllLink } from '@/components/organisms/PopularLinksList/utils/makeViewAllLink';
import { ContentComponentSection } from '@/components/atoms/ContentComponentSection';
import { Row, Col } from '@/components/atoms/Grid';
import { Card } from '@/components/atoms/Card';
import { SimpleLink } from '@/components/atoms/SimpleLink';
import { HeadingLevels } from '@/components/atoms/HeadingLevels';
import type { ServerLogger } from '@/server/logger';

import styles from './index.module.scss';

export interface TopLocationAndServicesProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export const defaultConfiguration = {
  backgroundColor: vars.quantumverse.reskinColors.white,
};

const TopLocationAndServicesSchema = {
  translatedEntities: z.object({
    heading: z.union([CmsRichTextSchema, CmsShortTextSchema]).optional(),
    subheadingHighlighted: z.union([CmsRichTextSchema, CmsShortTextSchema]).optional(),
    subheading: z.union([CmsRichTextSchema, CmsShortTextSchema]).optional(),
  }),
  configuration: z
    .object({
      backgroundColor: z.string().optional(),
    })
    .default(defaultConfiguration),
  serverSideData: z.object({
    segment: z.string(),
    /**
     * @note `pick` used to not pass to the client more data than the component needs.
     */
    topCollectionItems: z.object({
      area: CmsAreaItemSchema.pick({ name: true, slug: true }).array().optional(),
      product: CmsProductItemSchema.pick({ name: true, slug: true }).array().optional(),
    }),
  }),
};

export type TopLocationAndServicesTranslatedEntities = SchemaType<
  (typeof TopLocationAndServicesSchema)['translatedEntities']
>;
export type TopLocationAndServicesConfiguration = SchemaType<
  (typeof TopLocationAndServicesSchema)['configuration']
>;
export type TopLocationAndServicesServerSideData = SchemaType<
  (typeof TopLocationAndServicesSchema)['serverSideData']
>;

/**
 * A content component that links to the most popular Services and Locations.<br />
 * https://www.notion.so/fixter/TopLocationsAndServices-e1f0294305f64c3b93f61081d8d1e489
 */
export const TopLocationAndServices: ContentComponent<
  TopLocationAndServicesTranslatedEntities,
  TopLocationAndServicesConfiguration,
  TopLocationAndServicesProps,
  TopLocationAndServicesServerSideData
> = function TopLocationAndServices({
  translatedEntities,
  configuration,
  serverSideData = { segment: Segment.Fixter_UK, topCollectionItems: {} },
  className,
  ...props
}): JSX.Element {
  const { heading, subheading, subheadingHighlighted } = translatedEntities;
  const { segment, topCollectionItems } = serverSideData;
  const AppContext = useAppContext<AppContextGlobal>();
  const { locale } = AppContext;

  const { backgroundColor } = merge(defaultConfiguration, configuration);

  const areaLinks =
    topCollectionItems.area?.map((item): ILink => makePopularAreaLink({}, item, segment as Segment)) || [];
  const viewAllAreaLink = makeViewAllLink('area', segment as Segment);

  const productLinks =
    topCollectionItems.product?.map((item, i): ILink => {
      return {
        ...makePopularProductLink({}, item, segment as Segment),
        type: i < 3 ? 5 : 3,
      };
    }) || [];
  const viewAllProductLink = makeViewAllLink('product', segment as Segment);

  // @NOTE Hack show all links
  const showAllLinks = locale === Locale.EN_GB;

  /**
   * Headings can be ShortText or RichText
   */
  const headingValue = useMemo(
    () => (heading && 'content' in heading ? renderRichTextContent(heading.content) : heading?.value),
    [heading]
  );
  const subHeadingValue = useMemo(
    () =>
      subheading && 'content' in subheading ? renderRichTextContent(subheading.content) : subheading?.value,
    [subheading]
  );
  const subHeadingHighlightedValue = useMemo(
    () =>
      subheadingHighlighted && 'content' in subheadingHighlighted
        ? renderRichTextContent(subheadingHighlighted.content)
        : subheadingHighlighted?.value,
    [subheadingHighlighted]
  );

  return (
    <ContentComponentSection>
      <Row className={styles.TopLocationsAndServicesWrapper}>
        <Col>
          <Card
            className={styles.TopLocationsAndServicesContainer}
            backgroundColors={backgroundColor}
            cornerRadius={4}
            {...props}
          >
            <div className="text-wrapper">
              {heading && (
                <HeadingLevels className={styles.heading} semanticLevel={3} styleLevel={2}>
                  {headingValue}
                </HeadingLevels>
              )}
              {subheadingHighlighted && (
                <div className={styles.SubheadingHighlighted}>{subHeadingHighlightedValue}</div>
              )}
              {subheading && <div className={styles.Subheading}>{subHeadingValue}</div>}
            </div>
            <div className={styles.LinksRow}>
              <Card className={styles.linksCard} cornerRadius={1}>
                <HeadingLevels className={styles.heading} semanticLevel={4} styleLevel={6}>
                  {getLinksListTitle('area', segment as Segment)}
                </HeadingLevels>
                <ol className={styles.linksList}>
                  {areaLinks.map(({ name, link, type = 3 }) => (
                    <li key={`tag-link-${kebabCase(name)}`}>
                      <SimpleLink href={link} text={name} type={type} />
                    </li>
                  ))}
                </ol>
                {showAllLinks && viewAllAreaLink && (
                  <Link
                    className={styles.viewAllLink}
                    type="M"
                    href={viewAllAreaLink?.link}
                    iconId="arrows/arrow-up-right"
                    purpleIcon
                  >
                    {viewAllAreaLink?.name}
                  </Link>
                )}
              </Card>
              <Card className={styles.linksCard} cornerRadius={1}>
                <HeadingLevels className={styles.heading} semanticLevel={4} styleLevel={6}>
                  {getLinksListTitle('product', segment as Segment)}
                </HeadingLevels>
                <ol className={styles.linksList}>
                  {productLinks.map(({ name, link, type = 3 }) => (
                    <li key={`tag-link-${kebabCase(name)}`}>
                      <SimpleLink href={link} text={name} type={type} />
                    </li>
                  ))}
                </ol>
                {showAllLinks && viewAllProductLink && (
                  <Link
                    className={styles.viewAllLink}
                    type="M"
                    href={viewAllProductLink?.link}
                    iconId="arrows/arrow-up-right"
                    purpleIcon
                  >
                    {viewAllProductLink?.name}
                  </Link>
                )}
              </Card>
            </div>
          </Card>
        </Col>
      </Row>
    </ContentComponentSection>
  );
};
TopLocationAndServices.displayName = 'TopLocationAndServices';
TopLocationAndServices.schema = TopLocationAndServicesSchema;

export const fetchServerSideData: ContentComponentFetchServerSideData<
  TopLocationAndServicesServerSideData
> = async ({ segment }, preview, container, req) => {
  const logger = container.get<ServerLogger>('dynamicRouteLogger');
  const log = logger
    .traceRequest(req)
    .child({ label: `${TopLocationAndServices.displayName}.fetchServerSideData` });

  log.info('Fetching top location and services data');

  const queryContext = { headers: req.headers };

  const topCollectionItems = zipObject(
    ['area', 'product'],
    await Promise.all([
      fetchAreaCollectionsData(
        {
          filter: {
            key: 'popularity',
            operator: 'lte',
            value: 10,
          },
          limit: 7,
          segment,
          preview,
        },
        queryContext
      ),
      fetchProductCollectionData(
        {
          filter: {
            key: 'popularity',
            operator: 'lte',
            value: 10,
          },
          limit: 7,
          segment,
          preview,
        },
        queryContext
      ),
    ])
  );

  return TopLocationAndServicesSchema.serverSideData.parse({
    segment,
    topCollectionItems,
  });
};
